import { useEffect, useState } from 'react';
import { OrgRole, roleAtLeast, type OrganizationSettings } from '@ddp/shared';
import Loading from '@carbon/react/es/components/Loading/Loading';
import Toggle from '@carbon/react/es/components/Toggle/Toggle';
import { InlineNotification } from '@carbon/react/es/components/Notification/Notification';

import { apiFetch } from '../api/client.js';
import { useAuthStore } from '../auth/store.js';
import { PageHeader } from '../components/PageHeader.js';
import { ArchitectureSchema } from '../features/admin/ArchitectureSchema.js';
import { useArchitectureHealth } from '../features/admin/use-architecture-health.js';

/**
 * Administration de l'**organisation** (ADR-0037 §1). Distincte de la console plateforme : réservée
 * à l'`OrgRole.ADMIN`/`OWNER` de l'organisation courante. Première section : l'état de l'architecture ;
 * la page accueillera plus tard le suivi des usages et des coûts (cloisonnés à l'organisation).
 *
 * La porte d'authentification suffit côté route ; le rôle est vérifié par l'API (404 sinon) et la page
 * se masque d'elle-même pour un rôle insuffisant.
 */
export function AdminOrgPage() {
  const role = useAuthStore((s) => s.currentUser?.memberships[0]?.role ?? null);
  const canView = role !== null && roleAtLeast(role, OrgRole.ADMIN);

  const { health, loading, error } = useArchitectureHealth('/architecture-health', canView);

  if (!canView) {
    return (
      <section className="ddp-page__section">
        <PageHeader title="Administration de l’organisation" />
        <InlineNotification
          kind="info"
          title="Accès réservé"
          subtitle="Réservé aux administrateurs de l’organisation."
          lowContrast
          hideCloseButton
        />
      </section>
    );
  }

  return (
    <section className="ddp-page__section ddp-adminorg">
      <PageHeader
        title="Administration de l’organisation"
        lead="L’état des services de l’architecture. Le suivi des usages et des coûts viendra ici."
      />

      <h2 className="ddp-adminorg__subtitle">État de l’architecture</h2>
      <p className="ddp-adminorg__hint">
        Vue d’ensemble des services et de leurs liens. Rafraîchie automatiquement. Le vert indique
        un service sain ; jaune une alerte mineure ; orange un problème imminent ; rouge une
        indisponibilité ; gris un service non activé.
      </p>

      {error && !health && (
        <InlineNotification
          kind="error"
          title="Erreur"
          subtitle={error}
          lowContrast
          hideCloseButton
        />
      )}

      {loading && !health ? (
        <Loading withOverlay={false} description="Chargement de l’état…" />
      ) : (
        health && <ArchitectureSchema health={health} />
      )}

      <VendorConsentSetting />
    </section>
  );
}

/**
 * Consentement au partage de l'état d'architecture avec l'éditeur du logiciel (ADR-0037 §4). Bascule
 * **auditée** côté API (endpoint dédié) : activation comme révocation laissent une trace. Souveraineté —
 * le client garde la main sur ce que le fournisseur peut observer de son instance.
 */
function VendorConsentSetting() {
  const [share, setShare] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    apiFetch<OrganizationSettings>('/organizations/current/settings')
      .then((s) => {
        if (active) setShare(s.shareHealthWithVendor);
      })
      .catch(() => {
        if (active) setShare(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const onToggle = async (next: boolean) => {
    setBusy(true);
    setError(null);
    const previous = share;
    setShare(next); // optimiste
    try {
      const updated = await apiFetch<OrganizationSettings>(
        '/organizations/current/health-consent',
        { method: 'PATCH', body: { shareHealthWithVendor: next } },
      );
      setShare(updated.shareHealthWithVendor);
    } catch (err) {
      setShare(previous); // retour arrière si l'écriture échoue
      setError(err instanceof Error ? err.message : 'Enregistrement impossible.');
    } finally {
      setBusy(false);
    }
  };

  if (share === null) return null;

  return (
    <div className="ddp-adminorg__consent">
      <h2 className="ddp-adminorg__subtitle">Partage avec l’éditeur</h2>
      <p className="ddp-adminorg__hint">
        Autorise l’éditeur du logiciel à consulter cette même vue de l’état de l’architecture, à des
        fins de supervision. Désactivé par défaut, révocable à tout moment. Chaque changement est
        journalisé.
      </p>
      <Toggle
        id="vendor-health-consent"
        size="sm"
        labelText="Partager l’état d’architecture avec l’éditeur"
        labelA="Non partagé"
        labelB="Partagé"
        toggled={share}
        disabled={busy}
        onToggle={(checked) => void onToggle(checked)}
      />
      {error && (
        <InlineNotification
          kind="error"
          title="Erreur"
          subtitle={error}
          lowContrast
          hideCloseButton
        />
      )}
    </div>
  );
}
